import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { ssoExchange } from '@/auth/api'
import { ApiError } from '@/api/client'

/**
 * /sso?code=... — точка входа для агентов Help (SupportHub): переход по кнопке
 * «Открыть Бэкофис» приводит сюда с одноразовым кодом в query. Страница сама
 * обменивает код на сессию (POST /v1/auth/sso-exchange) и уводит на дашборд —
 * форма логина агенту не показывается вообще.
 *
 * Код одноразовый и живёт ~60 секунд: если пользователь открыл эту ссылку
 * второй раз (например, из истории браузера) — обмен закономерно упадёт 401,
 * и мы честно предлагаем вернуться в Help и нажать кнопку заново.
 */
export function Sso(): React.JSX.Element {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithSession } = useAuth()
  const [error, setError] = useState<string | null>(null)
  // StrictMode/повторный рендер не должны бить обмен дважды — код одноразовый.
  const startedRef = useRef(false)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const code = params.get('code')
    if (!code) {
      setError('Ссылка неполная — код входа отсутствует')
      return
    }

    ssoExchange(code)
      .then(({ token, staff }) => {
        loginWithSession(token, staff)
        navigate('/', { replace: true })
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setError('Ссылка входа истекла — вернитесь в Help и нажмите «Открыть Бэкофис» ещё раз')
        } else {
          setError('Не удалось войти. Попробуйте ещё раз из Help.')
        }
      })
  }, [params, navigate, loginWithSession])

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <div className="auth__mark">8</div>
          <div className="auth__title">8Ball Бекофис</div>
          <div className="auth__sub">Вход через Help</div>
        </div>
        {error ? (
          <p role="alert" style={{ color: 'var(--danger, #e5484d)', fontSize: 14, textAlign: 'center' }}>
            {error}
          </p>
        ) : (
          <p style={{ fontSize: 14, textAlign: 'center', opacity: 0.7 }}>Входим…</p>
        )}
      </div>
    </div>
  )
}
